import {
    createContext,
    MutableRefObject,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {EventEmitter, first} from "nate-react-api-helpers";
import {getEditable, TransitionKey} from "./TableEditCell";
import {
    CellCustomize,
    Column,
    Group,
    lookupNestedProp,
    setNestedProp,
    TableContext,
    TableSelectionContext,
    TID
} from "./Table";
import {useSnackbar} from "../Snackbar";
import {RangeActionProvider} from "./RangeActionProvider";
import {useSyncedRef} from "../SyncedRef";
import {useUndoPush} from "./GlobalUndo";
import {DependencyFinder} from "./DependencyFinder";
import {LastActive} from "./LastActive";

type SelectionChange = {
    id: number;
    index: number;
    value: any;
    editing: boolean;
} |null

type SelectionRange = {
    fromId: number;
    fromIndex: number;
    toId: number;
    toIndex: number;
    ids: number[];
} | null;

type SelectedCursorChange = {
    element: HTMLElement;
}

export const errShowNoSnackInTable = "showNoSnackInTable";

export const EditContext = createContext({
    tableName: "error-invalid-context",
    onSelect: new EventEmitter<SelectionChange>(),
    onSelectedCursorMoved: new EventEmitter<SelectedCursorChange>(),
    onRange: new EventEmitter<SelectionRange>(),
    locked: false,
    dataRef: {
        current: [] as Group<any>[],
    } as MutableRefObject<Group<any>[]>,
    columnsRef: {
        current: [] as Column<any>[]
    },
    hasPopout: false,
    cellCustomize: null as CellCustomize<any>|null,
    setHasPopout: (input: boolean) => {},
    transition: (input: TransitionKey) => {},
    update: (input: any, bulk: boolean = false) => {
        return Promise.resolve<any>(null);
    },
    reload: (merge: boolean = false, ids: number[] | null = null) => Promise.resolve(),
})

type Dir = -1|1;

export function EditProvider<T extends TID>(props: PropsWithChildren<{
    columns: Column<T>[];
    data: Group<T>[];
    locked?: boolean;
    onChangeHook?: (src: T | null, dst: T) => void;
    onChange?: (input: T, previous: T | null) => Promise<any | {ids: number[]}>;
    onChangeBulk?: (input: T[], previous: (T | null)[]) => Promise<any | {ids: number[]}>;
    name: string;
    cellCustomize?: CellCustomize<T>
}>) {
    const onSelect = useMemo(() => new EventEmitter<SelectionChange>(), []);
    const onRange = useMemo(() => new EventEmitter<SelectionRange>(), []);
    const onSelectedCursorMoved = useMemo(() => new EventEmitter<SelectedCursorChange>(), []);

    const onChangeRef = useRef(props.onChange);
    onChangeRef.current = props.onChange;

    const onChangeBulkRef = useRef(props.onChangeBulk);
    onChangeBulkRef.current = props.onChangeBulk;

    const onChangeHookRef = useRef(props.onChangeHook);
    onChangeHookRef.current = props.onChangeHook;

    const columnsRef = useRef(props.columns);
    columnsRef.current = props.columns;

    const hasPendingRefresh = useRef(false);

    const tbl = useContext(TableContext);
    const reloadTable = tbl.reload;

    const selectionCtx = useContext(TableSelectionContext);
    const selectionCtxUpdate = selectionCtx.update;
    const selectionCtxOnRefresh = selectionCtx.onRefresh;
    const reloadTableRef = useRef(reloadTable);
    reloadTableRef.current = reloadTable;

    useEffect(() => {
        return selectionCtxOnRefresh(props.name, () => {
            return reloadTableRef.current(true);
        });
    }, [selectionCtxOnRefresh, props.name])

    useEffect(() => {
        const sub = onSelect.subscribeAndFireLast(value => {
            selectionCtxUpdate(props.name, value?.value || null);
        });
        return () => sub.cancel();
    }, [onSelect, props.name, selectionCtxUpdate]);

    const snack = useSnackbar();
    const snackError = snack.error;
    const snackSuccess = snack.success;
    const lockedRef = useSyncedRef(props.locked || false);

    const pushUndo = useUndoPush();

    const undo = useCallback(async (last: T) => {
        try {
            if(lockedRef.current) throw new Error("Can't edit a locked table")
            if(!onChangeRef.current) throw new Error("Missing updater");

            const rs = await onChangeRef.current(last, null);
            reloadTable(true);
            snackSuccess("Undone");
            return rs;
        } catch(e: any) {
            snackError(e.toString());
            reloadTable(true);
            return null;
        }

    }, [reloadTable, snackError, onChangeRef, lockedRef, snackSuccess])

    const setupUpdate = useCallback((input: T) => {
        let currentRef: T|null = null;
        let prev: T|null = null;

        if(input.id && input.id > 0) {
            dataRef.current.forEach(c => {
                const find = first(c.rows, r => r.id === input.id);
                if(find) currentRef = find
            });

            if(currentRef) {
                prev = Object.assign({}, currentRef);
                pushUndo(() => {
                    if(prev) return undo(prev)
                    return Promise.resolve()
                })
            }
        }

        if(currentRef) {
            clearDependenciesForCellChange(columnsRef.current, currentRef, input);
        }

        if(lockedRef.current) throw new Error("Can't edit a locked table")

        if(onChangeHookRef.current) {
            onChangeHookRef.current(currentRef, input);
        }

        // update object in-place for list re-render
        if(currentRef) {
            Object.assign(currentRef, input);
        }

        return {
            currentRef: currentRef,
            prev: prev,
        };
    }, [pushUndo, undo, columnsRef, lockedRef, onChangeHookRef]);

    const afterUpdate = useCallback((rs: any) => {
        let ids: number[] | null = null;
        try {
            if (rs && typeof rs === "object" && "ids" in rs && rs.ids instanceof Array && rs.ids.length > 0 && rs.ids.every(id => typeof id === "number")) {
                // only used targetted reload if less than 20 rows were updated
                // this prevents issues with GET requests having an overflow if query params
                if(rs.ids.length < 20) {
                    ids = rs.ids;
                }
            } else if(rs && typeof rs === "object" && "id" in rs && typeof rs.id === "number") {
                ids = [rs.id];
            }
        } catch (e) {
            ids = null;
            console.error(e);
        }

        reloadTable(true, ids);
    }, [reloadTable]);

    const update = useCallback(async (input: T) => {
        if(lockedRef.current) throw new Error("Can't edit a locked table")

        if(!onChangeRef.current) return;
        hasPendingRefresh.current = true;

        try {
            const {currentRef, prev} = setupUpdate(input);
            const rs = await onChangeRef.current(input, prev);

            if(!currentRef) {
                const prev = Object.assign({}, rs, {
                    archived: true,
                });
                pushUndo(() => undo(prev))
            }

            afterUpdate(rs);
            snackSuccess("Updated");
            return rs;
        } catch(e: any) {
            let showError = true;

            if(typeof e === "object" && "message" in e) {
                if(e.message === errShowNoSnackInTable) {
                    showError = false;
                }
            }

            if(showError) {
                snackError(e.toString());
            }

            reloadTable(false);
            return null;
        }
    }, [reloadTable, snackError, snackSuccess, pushUndo, undo, onChangeRef, afterUpdate, setupUpdate, lockedRef]);

    const isLastActive = useMemo(() => new LastActive(), []);

    const dataRef = useRef(props.data)
    dataRef.current = props.data;

    // restoreRows puts the pre-update copies back into the table's in-memory rows after a
    // failed bulk update, so the table shows the saved values again without a page reload
    const restoreRows = useCallback((previousRows: (T | null)[]) => {
        for(const prev of previousRows) {
            if(!prev) continue;

            dataRef.current.forEach(g => {
                const row = first(g.rows, r => r.id === prev.id);
                if(row) Object.assign(row, prev);
            });
        }
    }, []);

    const updateBulk = useCallback(async (input: T[]) => {
        // if one, use regular flow b/c this will sometimes trigger special functionality
        if(input.length === 1) {
            return update(input[0]);
        }

        if(lockedRef.current) throw new Error("Can't edit a locked table")

        const bulkUpdater = onChangeBulkRef.current;
        if(!bulkUpdater) {
            const onChange = onChangeRef.current;
            if(!onChange) {
                throw new Error("Missing updater");
            }

            const active = isLastActive.start();

            const batcher = new Batcher(5);
            snack.loading(`Updating 1 of ${input.length} rows...`);
            batcher.onProgress(() => {
                snack.loading(`Updating ${input.length - batcher.queue.length} of ${input.length} rows...`);
            });

            const previousRows = input.map(itm => setupUpdate(itm).prev);

            try {
                const promises = input.map((itm, idx) => batcher.add(() => onChange(itm, previousRows[idx])));

                const result = await Promise.allSettled(promises);
                result.forEach(r => {
                    if (r.status === "rejected") {
                        throw new Error(r.reason);
                    }
                });

                afterUpdate(null);

                if (active.finishAndIsLast()) {
                    snackSuccess("Updated");
                }
            } catch (e: any) {
                snackError(e.toString());
                restoreRows(previousRows);
                reloadTable(false);
                throw e;
            } finally {
                active.finish();
            }

            return;
        }

        hasPendingRefresh.current = true;

        const active = isLastActive.start();
        const previousRows = input.map(itm => setupUpdate(itm).prev);

        try {
            for(let i = 0; i < input.length; i += 200) {
                snack.loading(`Updating ${i}-${Math.min(i + 200, input.length)} of ${input.length} rows...`);

                const chunk = input.slice(i, i + 200);
                const previousChunk = previousRows.slice(i, i + 200);
                const rs = await bulkUpdater(chunk, previousChunk);
                afterUpdate(rs);
            }

            if(active.finishAndIsLast()) {
                snackSuccess("Updated");
            }

            return null;
        } catch(e: any) {
            let showError = true;

            if(typeof e === "object" && "message" in e) {
                if(e.message === errShowNoSnackInTable) {
                    showError = false;
                }
            }

            if(showError) {
                snackError(e.toString());
            }

            restoreRows(previousRows);
            reloadTable(false);
            throw e;
        } finally {
            active.finish();
        }

    }, [onChangeBulkRef, snackError, snackSuccess, snack, isLastActive, afterUpdate, setupUpdate, lockedRef, update, restoreRows, reloadTable]);

    const transition = useCallback((key: TransitionKey) => {
        const dt = dataRef.current

        const nextIndex = (basis: number = -1, direction: Dir = 1) => {
            basis += direction;

            for(let i = basis; i >= 0 && i < columnsRef.current.length; i += direction) {
                if(columnsRef.current[i].editable) {
                    return i;
                }
            }

            return -1;
        }

        if(!onSelect.lastValue) {
            if(dt.length === 0) return;
            onSelect.emit({
                id: dt[0].rows[0].id,
                index: nextIndex(),
                value: {id: dt[0].rows[0].id},
                editing: false,
            });
            return;
        }

        const selected = onSelect.lastValue;

        const downId = () => {
            for(let i = 0; i < dt.length; i++) {
                for(let j = 0; j < dt[i].rows.length; j++) {
                    if (dt[i].rows[j].id === selected.id) {
                        if(dt[i].rows.length > j+1) {
                            return dt[i].rows[j + 1]; // next row in group
                        } else if(dt.length > i + 1 && dt[i+1].rows.length > 0) {
                            return dt[i+1].rows[0]; // first row of next group
                        } else {
                            return null; // can't find the next cell (or it's a big jump)
                        }
                    }
                }
            }

            return null;
        }

        const upId = () => {
            for(let i = 0; i < dt.length; i++) {
                for(let j = 0; j < dt[i].rows.length; j++) {
                    if (dt[i].rows[j].id === selected.id) {
                        if(j-1 >= 0) {
                            return dt[i].rows[j - 1]; // next row in group
                        } else if(i > 0 && dt[i-1].rows.length > 0) {
                            const grp = dt[i-1];
                            return grp.rows[grp.rows.length-1]; // last row of prev group
                        } else {
                            return null; // can't find the next cell (or it's a big jump)
                        }
                    }
                }
            }

            return null;
        }

        let next, id, index;

        switch(key) {
            case "down":
                // don't trigger refresh here, should be done beside the update call
                next = downId()
                if(!next) {
                    onSelect.emit(null);
                } else {
                    onSelect.emit({
                        id: next.id,
                        index: selected.index,
                        value: next,
                        editing: false,
                    });
                }
                break;
            case "prev":
            case "left":
                index = nextIndex(selected.index, -1);
                id = selected.id;

                if(index === -1) {
                    return;
                }

                onSelect.emit({
                    id: id,
                    index: index,
                    value: selected.value,
                    editing: false,
                });
                break;
            case "next":
            case "right":
                index = nextIndex(selected.index, 1);
                id = selected.id;

                if(index === -1) {
                    return;
                }

                onSelect.emit({
                    id: id,
                    index: index,
                    value: selected.value,
                    editing: false,
                });
                break;
            case "up":
                // don't trigger refresh here, should be done beside the update call
                next = upId()
                if(!next) {
                } else {
                    onSelect.emit({
                        id: next.id,
                        index: selected.index,
                        value: next,
                        editing: false,
                    });
                }
                break;
            case "blur":
                onSelect.emit(null);
                break;
        }

    }, [onSelect]);

    const locked = props.locked || false;
    const [hasPopout, setHasPopout] = useState(false);
    const cellCustomize = props.cellCustomize;
    const tableName = props.name;

    const ctx = useMemo(() => ({
        tableName: tableName,
        onSelect: onSelect,
        onRange: onRange,
        onSelectedCursorMoved: onSelectedCursorMoved,
        update: update,
        dataRef: dataRef,
        columnsRef: columnsRef,
        transition: transition,
        locked: locked,
        hasPopout: hasPopout,
        cellCustomize: cellCustomize || null,
        setHasPopout: setHasPopout,
        reload: reloadTable,
    }), [tableName, onSelect, update, dataRef, transition, reloadTable, onRange, locked, hasPopout, onSelectedCursorMoved, cellCustomize]);

    return (
        <EditContext.Provider value={ctx}>
            {props.children}
            {props.onChange && <RangeActionProvider data={props.data} onChange={updateBulk} columns={props.columns} />}
        </EditContext.Provider>
    );
}

function clearDependenciesForCellChange<T extends TID>(columns: Column<T>[], src: T, dst: T) {
    const dep = new DependencyFinder<T>(src, dst);

    for(const column of columns) {
        const editable = getEditable(column, src)

        if(editable?.type === "select") {
            editable.options(dep.forColumn(column))
        } else if(editable?.type === "lookup") {
            editable.options("", dep.forColumn(column))
        }
    }

    // a dependent cell that this same update changed was set on purpose (e.g. a pasted row that
    // changes both opening type and handing), so only cells left untouched are cleared
    dep.keysToClear().map(k => {
        // @ts-ignore
        if(lookupNestedProp(src, k) !== lookupNestedProp(dst, k)) return;
        // @ts-ignore
        setNestedProp(dst, k, "");
    })
}


type Callback = () => void

class Batcher {
    nParallel: number;
    active: number = 0;
    queue: Callback[] = [];
    progress: Callback = () => {}

    constructor(nParallel: number) {
        this.nParallel = nParallel;
    }

    add<T>(cb: () => Promise<T>) {
        return new Promise<T>((resolve, reject) => {
            this.queue.push(async () => {
                const update = () => {
                    this.active--;
                    this.pump();
                    this.progress();
                }

                try {
                    const r = await cb();
                    update();
                    resolve(r);
                } catch (e) {
                    update();
                    reject(e);
                }
            });

            this.pump();
        })
    }

    pump() {
        if(this.active >= this.nParallel) {
            return;
        }

        const first = this.queue.shift();
        if(!first) return;

        this.active++;
        first();
    }

    onProgress(cb: Callback) {
        this.progress = cb;
    }

    pending() {
        return this.active + this.queue.length;
    }
}
